import { lazy } from 'react';
import {
  ELSCommonUIConstants,
  ELSSecurityRoutes
} from '@els/els-ui-common-react';
import { RoutePath } from './app.constants';

export type RouteConfig = {
  path: RoutePath;
  isPrivate: boolean;
  exact: boolean;
  data: {
    analytics: {
      pageType: string;
      pageName: string;
    };
  };
}

export const getRoutePathByPath = (path: string): RoutePath => {
  return Object.values(RoutePath)
    .sort((a, b) => a.length - b.length) // Sort by length to match longer paths first
    .find((routePath) => path.startsWith(routePath));
};

export const convertPathToName = (path: string): string => {
  // remove leading slash
  return path.replace(/^\//, '');
};

const getDefaultRouteConfig = (path: RoutePath): RouteConfig => {
  return {
    path,
    isPrivate: false,
    exact: false,
    data: {
      analytics: {
        pageType: ELSCommonUIConstants.analytics.pageTypes.genericNav,
        pageName: `naa:${convertPathToName(path)}`
      }
    }
  }
};

export const RouteConfigMap: Record<RoutePath, RouteConfig> = {
  [RoutePath.LOGOUT]: getDefaultRouteConfig(RoutePath.LOGOUT),
  [RoutePath.UNEXPECTED_ERROR]: getDefaultRouteConfig(RoutePath.UNEXPECTED_ERROR),
  [RoutePath.HELP]: getDefaultRouteConfig(RoutePath.HELP),
  [RoutePath.TIMEOUT]: getDefaultRouteConfig(RoutePath.TIMEOUT),
  [RoutePath.NOT_AUTHORIZED]: getDefaultRouteConfig(RoutePath.NOT_AUTHORIZED),
  [RoutePath.PAGE_NOT_FOUND]: getDefaultRouteConfig(RoutePath.PAGE_NOT_FOUND),
  [RoutePath.MAINTENANCE]: getDefaultRouteConfig(RoutePath.MAINTENANCE),
  [RoutePath.LOGIN]: getDefaultRouteConfig(RoutePath.LOGIN),
  [RoutePath.APP_LINK_REDIRECT]: getDefaultRouteConfig(RoutePath.APP_LINK_REDIRECT),
  [RoutePath.SUPPORT]: {
    ...getDefaultRouteConfig(RoutePath.SUPPORT),
    isPrivate: true
  },
  [RoutePath.AI_CHAT]: {
    ...getDefaultRouteConfig(RoutePath.AI_CHAT),
    isPrivate: true
  },
  [RoutePath.AI_CHAT_PERFORMANCE]: {
    ...getDefaultRouteConfig(RoutePath.AI_CHAT_PERFORMANCE),
    isPrivate: true
  },
  [RoutePath.AI_CHAT_ADMIN]: {
    ...getDefaultRouteConfig(RoutePath.AI_CHAT_ADMIN),
    isPrivate: true
  },
  [RoutePath.AUTHORING_POC]: {
    ...getDefaultRouteConfig(RoutePath.AUTHORING_POC),
    isPrivate: true
  },
  [RoutePath.YOUR_CHATS]: {
    ...getDefaultRouteConfig(RoutePath.YOUR_CHATS),
    isPrivate: true
  },
  [RoutePath.YOUR_BOOKS]: {
    ...getDefaultRouteConfig(RoutePath.YOUR_BOOKS),
    isPrivate: true
  },
  [RoutePath.HOW_IT_WORKS]: {
    ...getDefaultRouteConfig(RoutePath.HOW_IT_WORKS),
    isPrivate: true
  },
  [RoutePath.QUIZ_QUESTIONS]: {
    ...getDefaultRouteConfig(RoutePath.QUIZ_QUESTIONS),
    isPrivate: true
  },
  [RoutePath.FLASHCARDS]: {
    ...getDefaultRouteConfig(RoutePath.FLASHCARDS),
    isPrivate: true
  }
};

type AppRoute = RouteConfig & {
  component: React.LazyExoticComponent<React.ComponentType<unknown>>;
}

export const getAppRoutes = (): AppRoute[] => [
  ...ELSSecurityRoutes.getSecurityRoutes()
    .filter((route) => {
      return route.path !== `/${ELSCommonUIConstants.security.States.Admin}`;
    }),
  {
    ...RouteConfigMap[RoutePath.APP_LINK_REDIRECT],
    component: lazy(() => import(/* webpackChunkName: "AppLinkRedirect.page" */ '../../pages/app-link-redirect/AppLinkRedirect.page')),
  }, {
    ...RouteConfigMap[RoutePath.LOGIN],
    component: lazy(() => import(/* webpackChunkName: "Login.page" */ '../../pages/login/Login.page')),
  }, {
    ...RouteConfigMap[RoutePath.SUPPORT],
    component: lazy(() => import(/* webpackChunkName: "Support.page" */ '../../pages/support/Support.page')),
  }, {
    ...RouteConfigMap[RoutePath.AI_CHAT],
    component: lazy(() => {
      return import(/* webpackChunkName: "AiChat.page" */ '../../pages/ai-chat/AiChat.page');
    }),
  }, {
    ...RouteConfigMap[RoutePath.AI_CHAT_PERFORMANCE],
    component: lazy(() => {
      return import(/* webpackChunkName: "AiChatPerformance.page" */ '../../pages/ai-chat-performance/AiChatPerformance.page');
    }),
  }, {
    ...RouteConfigMap[RoutePath.AI_CHAT_ADMIN],
    component: lazy(() => {
      return import(/* webpackChunkName: "AiChatAdmin.page" */ '../../pages/ai-chat-admin/AiChatAdmin.page');
    }),
  }, {
    ...RouteConfigMap[RoutePath.AUTHORING_POC],
    component: lazy(() => {
      return import(/* webpackChunkName: "Authoring.page" */ '../../pages/authoring/Authoring.page');
    }),
  }, {
    ...RouteConfigMap[RoutePath.YOUR_CHATS],
    component: lazy(() => {
      return import(/* webpackChunkName: "YourChats.page" */ '../../pages/your-chats/YourChats.page');
    }),
  }, {
    ...RouteConfigMap[RoutePath.YOUR_BOOKS],
    component: lazy(() => {
      return import(/* webpackChunkName: "YourBooks.page" */ '../../pages/your-books/YourBooks.page');
    }),
  }, {
    ...RouteConfigMap[RoutePath.HOW_IT_WORKS],
    component: lazy(() => {
      return import(/* webpackChunkName: "HowItWorks.page" */ '../../pages/how-it-works/HowItWorks.page');
    }),
  }, {
    ...RouteConfigMap[RoutePath.QUIZ_QUESTIONS],
    component: lazy(() => {
      return import(/* webpackChunkName: "QuizQuestions.page" */ '../../pages/quiz-questions/QuizQuestions.page');
    }),
  }, {
    ...RouteConfigMap[RoutePath.FLASHCARDS],
    component: lazy(() => {
      return import(/* webpackChunkName: "Flashcards.page" */ '../../pages/flashcards/Flashcards.page');
    }),
  }];
